import { ObjectTools } from '../../tools/object-tools';
import { SolSelectValue } from './sol-select-value';
import { SelectTools } from '../../tools/select-tools';
import { SolText } from './sol-text';
import { SolIcon } from '@sol/icons';
import { SolLayout } from './sol-layout';

export type SolButtonGroupSize = 'default' | 'small';

export interface SolButtonGroupButton {
  id: string | undefined;
  name: SolText;
  icon: SolIcon | undefined;
  disabled: boolean;
  selected: boolean;
}

export class SolButtonGroup {
  private constructor(
    public readonly disabled: boolean,
    public readonly value: string | undefined,
    public readonly availableValues: SolSelectValue[],
    public readonly size: SolButtonGroupSize,
    public readonly layout: SolLayout,
    public readonly hostClass: string,
    public readonly buttons: SolButtonGroupButton[],
  ) {}

  public static of(): SolButtonGroup {
    return new SolButtonGroup(
      false,
      undefined,
      [],
      'default',
      SolLayout.of().withType('horizontal').withGap('4').withP('4'),
      'sol-button-group',
      [],
    );
  }

  public withDisabled(disabled: boolean): SolButtonGroup {
    return this.copy({ disabled }).calculateHostClass();
  }

  public withValue(value: string | undefined): SolButtonGroup {
    return this.copy({ value }).calculateButtons();
  }

  public withAvailableValues(availableValues: SolSelectValue[]): SolButtonGroup {
    return this.copy({ availableValues }).calculateButtons();
  }

  public withSize(size: SolButtonGroupSize): SolButtonGroup {
    return this.copy({ size });
  }

  public withLayout(layout: SolLayout): SolButtonGroup {
    return this.copy({ layout });
  }

  private calculateHostClass(): SolButtonGroup {
    let hostClass = 'sol-button-group';
    if (this.disabled) {
      hostClass += ' sol-button-group-disabled';
    }
    return this.copy({ hostClass });
  }

  private calculateButtons(): SolButtonGroup {
    const buttons: SolButtonGroupButton[] = this.availableValues.map(value => ({
      id: value.id,
      name: SelectTools.toSolText(value, value.id),
      icon: value.icon,
      disabled: !!value.disabled,
      selected: value.id === this.value,
    }));
    return this.copy({ buttons });
  }

  private copy(props: Partial<SolButtonGroup>): SolButtonGroup {
    return ObjectTools.copy<SolButtonGroup>(this, props);
  }
}
